import { DatePipe } from "@angular/common";
import { Component, DestroyRef, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { AuthorizationRequestService, LookupService } from "src/app/core";
import { AuthorizationCertPeriod, RetroactiveDateRangeCalc, ServiceLineGridDetail } from "@core/model";
import { ReferenceByEntity } from "@core/model/reference-by-entity";
import { ReferenceByEntityService } from "@core/service/reference-by-entity.service";
import { ReviewPeriod } from '@shared/model';
import { ArrayUtilities } from '@shared/utility';
import moment from "moment";
import { SelectItem } from "primeng/api";
import { DynamicDialogRef, DynamicDialogConfig } from "primeng/dynamicdialog";
import { finalize, Subscription } from "rxjs";

type RequestType = 'Subsequent' | 'Initial';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'request-visits-dialog',
    templateUrl: './request-visits-dialog.component.html',
    styleUrls: ['./request-visits-dialog.component.scss'],
    standalone: false
})
export class RequestVisitsDialogComponent implements OnInit {
  showHeaderForDateSelect = false;
  serviceLineEditForm: FormGroup;
  requestType: RequestType;
  subsequentRequestType = 'Subsequent';
  tenantId: string;
  noRetroPeriodMessage = 'No Retro Period';
  isExternalUser = false;
  currentEditStep: number;
  editDetail: ServiceLineGridDetail = {};
  requestedVisitsValid = true;
  editInstructions = '';
  showNA = true;
  isDisabled = false;
  isGettingRetroDateRange = false;
  initialRequestType = 'Initial';
  hh30DayReviewPeriods = false;
  isHHRequestProcessUpdates = false;
  showDateDisciplineFirstVisit = false;
  selectedCertPeriod: AuthorizationCertPeriod;
  dateTimeFaxReceived: string;
  startOfCareDate: string;
  hasContractFlow = false;
  contractStartDate: string;
  hideReviewPeriodField = false;
  woundOstomyCriterias: ReferenceByEntity[] = [];
  minDateLastVisitDeliveredDate: Date = null;
  maxDateLastVisitDeliveredDate: Date = null;
  reviewPeriodOptions: SelectItem[] = [];
  urgencies: SelectItem[];
  reviewPeriods: ReviewPeriod[] = [];
  selectedReviewPeriod: ReviewPeriod;
  retroDateRangeEffectiveToDate: string;
  preselectReviewPeriod: string;
  createNextReviewPeriodClicked: false;
  showTimelyAndTotalVisits = true;
  noRetroVisitsForCSD = false;
  retroEffectiveDatePriorToSelectedReviewPeriod = false;
  reimbursementType: string;
  participationStatus: boolean;
  reviewPeriodException: boolean;
  isReviewPeriodDefaultA = false;
  reviewPeriodA = "ReviewPeriodA";
  isServiceLineReauth: boolean;
  private _allowToChooseCriteria = false;

  private unsubscribeRetroVisitsRequested: Subscription | undefined;
  private unsubscribeTimelyVisitsRequested: Subscription | undefined;

  private set allowToChooseCriteria(value: boolean) {
    this._allowToChooseCriteria = value;

    if (!value) {
      this.initMemberCriteriaFormControls();
    } else {
      this.updateMemberCriteriaSelectedDisabledState();
    }
  }

  get allowToChooseCriteria(): boolean {
    return this._allowToChooseCriteria;
  }

  get memberCriteriaFormArray(): FormArray {
    return <FormArray>this.serviceLineEditForm.controls.memberCriteriaIds as FormArray;
  }

  get memberCriteriaValid(): boolean {
    if (!this.isCurrentEditSNType) return true;

    const anyCriteriaSelected = this.memberCriteriaFormArray.controls.some(control => control.value.selected);

    return this.allowToChooseCriteria ? anyCriteriaSelected : !anyCriteriaSelected;
  }

  get isCurrentEditSNType(): boolean {
    return this.editDetail.skillCode === 'SN';
  }

  get reviewPeriodTypeControl(): FormControl {
    return this.serviceLineEditForm.get('reviewPeriodType') as FormControl;
  }

  get urgencyControl(): FormControl {
    return this.serviceLineEditForm.get('urgency') as FormControl;
  }

  get timelyVisitsRequestedControl(): FormControl {
    return this.serviceLineEditForm.controls['timelyVisitsRequested'] as FormControl;
  }

  get dateLastVisitDeliveredNAControl(): FormControl {
    return this.serviceLineEditForm.controls['dateLastVisitDeliveredNA'] as FormControl;
  }

  get additionalCommentsControls(): FormControl {
    return this.serviceLineEditForm.controls['additionalComments'] as FormControl;
  }

  get isInitRequest(): boolean {
    return this.requestType === this.initialRequestType;
  }

  get isInitialServiceLinesSubsequentRequest(): boolean {
    return this.showNA && !this.isInitRequest;
  }

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private referenceByEntityService: ReferenceByEntityService,
    private authorizationRequestService: AuthorizationRequestService,
    private lookupService: LookupService,
    private datePipe: DatePipe,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    const {
      requestType, tenantId, isExternalUser, reviewPeriods,
      editDetail, showNA, disableReviewPeriod,
      selectedCertPeriod, dateTimeFaxReceived, startOfCareDate,
      hasContractFlow, contractStartDate, hideReviewPeriodField,
      preselectReviewPeriod, hh30DayReviewPeriods, reviewPeriodOptions,
      createNextReviewPeriodClicked, noRetroVisitsForCSD, isHHRequestProcessUpdates,
      participationStatus, reimbursementType, reviewPeriodException} = this.config.data;

      this.editDetail = editDetail;
      this.requestType = requestType;
      this.tenantId = tenantId;
      this.isExternalUser = isExternalUser;
      this.showNA = showNA;
      this.selectedCertPeriod = selectedCertPeriod;
      this.dateTimeFaxReceived = dateTimeFaxReceived;
      this.startOfCareDate = startOfCareDate;
      this.hasContractFlow = hasContractFlow;
      this.contractStartDate = contractStartDate;
      this.hideReviewPeriodField = hideReviewPeriodField;
      this.reviewPeriods = reviewPeriods;
      this.reviewPeriodOptions = reviewPeriodOptions;
      this.hh30DayReviewPeriods = hh30DayReviewPeriods;
      this.isHHRequestProcessUpdates = isHHRequestProcessUpdates;
      this.preselectReviewPeriod = preselectReviewPeriod;
      this.createNextReviewPeriodClicked = createNextReviewPeriodClicked;
      this.noRetroVisitsForCSD = noRetroVisitsForCSD;
      this.participationStatus = participationStatus;
      this.reimbursementType = reimbursementType;
      this.reviewPeriodException = reviewPeriodException;
      /*
        showNA is used to determine if the service line is initial or reauthorization in this file,
        In auth-request-services.component.ts showNA is set to true when it is an initial request.
      */
      this.isServiceLineReauth= !this.showNA
      this.initData(preselectReviewPeriod, disableReviewPeriod);
      this.allowToChooseCriteria = true;
  }

  private initData(preselectReviewPeriod: string, disableReviewPeriod: boolean) {
    this.minDateLastVisitDeliveredDate = new Date(moment(this.startOfCareDate).format('l'));
    this.currentEditStep = this.isInitRequest ? 1 : 0;

    if (!this.hasContractFlow && this.selectedCertPeriod !== undefined) {
      this.maxDateLastVisitDeliveredDate = new Date(moment(this.selectedCertPeriod.effectiveToDate, 'l').add(-1, 'days').format('l'));
    }

    this.buildServiceLineEditForm(this.editDetail);

    if (this.createNextReviewPeriodClicked){
      this.editDetail.createNextReviewPeriodClicked = this.createNextReviewPeriodClicked;
    }

    if (this.requestType !== this.initialRequestType) {
      this.getUrgenciesList();

      if (this.hh30DayReviewPeriods && !this.hideReviewPeriodField) {
        this.manageUrgencyControl();
      }
    }

    this.setFormSubscriptions();

    if (preselectReviewPeriod) {
      this.reviewPeriodTypeControl.setValue(preselectReviewPeriod);
    }

    if (disableReviewPeriod) {
      this.reviewPeriodTypeControl.disable();
    }

    this.setEditInstructions(this.currentEditStep);
    this.isReviewPeriodADefaulted();
  }

  private getUrgenciesList(): void {
    this.lookupService.getTenantUrgencies(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(r => {
        const filteredUrgencies = r.filter(e => e.tenantId === this.tenantId && e.isAuthRequest);
        this.urgencies = ArrayUtilities.arrayToSelectItemArrayByKeyAndValue(filteredUrgencies, 'urgency', 'urgency', false);
      });
  }

  private manageUrgencyControl(): void {
    this.hideReviewPeriodField = true;

    this.urgencyControl.valueChanges.subscribe(res => {
      this.hideReviewPeriodField = !res;
    });
  }

  private reviewPeriodFormWatchOnValueChanges():void {
    this.reviewPeriodTypeControl.valueChanges.subscribe(x => {
      this.reviewPeriodFormWatch(x);
    });
  }

  private buildServiceLineEditForm(sl: ServiceLineGridDetail = {}): void {
    this.serviceLineEditForm = new FormGroup({
      dateTimeFaxReceived: new FormControl(sl.dateTimeFaxRecieved),
      urgency: new FormControl(sl.urgency),
      hcpcCode: new FormControl(sl.hcpcCode),
      previouslyAllowedVisits: new FormControl(sl.totalPreviouslyApprovedVisits),
      dateLastVisitDelivered: new FormControl(sl.dateLastVisitDelivered, Validators.required),
      dateLastVisitDeliveredNA: new FormControl(false),
      retroDateRange: new FormControl(''),
      retroVisitsRequested: new FormControl(sl.retroVisitsRequested, [Validators.min(0), Validators.max(999), Validators.maxLength(3), Validators.pattern('[0-9]*')]),
      timelyVisitsRequested: new FormControl(sl.timelyVisitsRequested, [
        Validators.min(0), Validators.max(9999), Validators.maxLength(4), Validators.pattern('[0-9]*')
      ]),
      totalVisitsRequested: new FormControl(sl.totalVisitsRequested),
      memberCriteriaIds: new FormArray([]),
      reviewPeriodType: new FormControl(''),
      additionalComments: new FormControl(sl.additionalComments)
    });

    this.initMemberCriteriaOptions();

    if (this.requestType === this.subsequentRequestType) {
      this.urgencyControl.setValidators([Validators.required]);
      this.urgencyControl.updateValueAndValidity();
    }
  }

  private updateMemberCriteriaSelectedDisabledState() {
    this.memberCriteriaFormArray?.controls?.forEach(i => this.allowToChooseCriteria
      ? i.get('selected')?.enable()
      : i.get('selected')?.disable());
  }

  private initMemberCriteriaOptions(): void {
    this.referenceByEntityService.getDropdownValues(this.tenantId, "Service Line", "Wound Ostomy Member Criteria")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(woundOstomyCriterias => {
        this.woundOstomyCriterias = woundOstomyCriterias;

        this.initMemberCriteriaFormControls();
      });
  }

  private initMemberCriteriaFormControls(): void {
    this.memberCriteriaFormArray.clear();
    this.woundOstomyCriterias.forEach((el) =>
      this.memberCriteriaFormArray.push(new FormGroup({
        value: new FormControl({ id: el.referenceByEntityId, label: el.description }),
        selected: new FormControl({ value: false, disabled: !this.allowToChooseCriteria }),
      }))
    );

    this.setupCheckboxDependencies();
  }

  private setupCheckboxDependencies(): void {
    const controls = this.memberCriteriaFormArray.controls;
    const notApplicableIndex = controls.length - 1;
    const notApplicableControl = controls[notApplicableIndex]?.get('selected');

    // Watch all controls except "Not Applicable"
    controls.slice(0, -1).forEach((ctrl, index) => {
      ctrl.get('selected')?.valueChanges.subscribe(() => {
        const anyOtherChecked = controls.slice(0, -1).some(c => c.get('selected')?.value === true);
        if (anyOtherChecked) {
          notApplicableControl?.disable({ emitEvent: false });
          notApplicableControl?.setValue(false, { emitEvent: false });
        } else {
          notApplicableControl?.enable({ emitEvent: false });
        }
      });
    });

    // Watch "Not Applicable"
    notApplicableControl?.valueChanges.subscribe((checked) => {
      controls.slice(0, -1).forEach(ctrl => {
        const control = ctrl.get('selected');
        if (checked) {
          control?.disable({ emitEvent: false });
          control?.setValue(false, { emitEvent: false });
        } else if (this.allowToChooseCriteria) {
          control?.enable({ emitEvent: false });
        }
      });
    });
  }

  private clearOutEditDetail(): void {
    this.serviceLineEditForm.controls['dateLastVisitDelivered'].setValue(null);
    this.dateLastVisitDeliveredNAControl.setValue(false);
    this.serviceLineEditForm.controls['retroVisitsRequested'].setValue(null);
    this.timelyVisitsRequestedControl.setValue(null);
    this.serviceLineEditForm.controls['retroDateRange'].setValue(null);
    this.serviceLineEditForm.controls['totalVisitsRequested'].setValue(null);
    this.additionalCommentsControls.setValue(null);
    this.initMemberCriteriaFormControls();
    this.serviceLineEditForm.updateValueAndValidity();
  }

  private setFormSubscriptions(): void {
    this.serviceLineEditFormWatch();
    this.reviewPeriodFormWatchOnValueChanges();
  }

  private serviceLineEditFormWatch(): void {
    this.serviceLineEditForm.valueChanges.subscribe(() => {
      this.loadServiceLineEditFormFieldChanges(this.serviceLineEditForm.getRawValue());

      const hasRetroPeriod = this.editDetail.retroDateRange !== this.noRetroPeriodMessage;

      let retroDateRangeStart: Date | null = null;
      let retroDateRangeEnd: Date | null = null;

      if (this.editDetail.retroDateRange) {
        [retroDateRangeStart, retroDateRangeEnd] = this.editDetail.retroDateRange.split('-').map((s) => new Date(s.trim()));
      }

      const isRetroVisitsAmountValid = !this.isServiceLineReauth
        ? this.serviceLineEditForm.controls['dateLastVisitDelivered'].value > retroDateRangeEnd
          ? this.editDetail.retroVisitsRequested != null
          : (this.editDetail.retroVisitsRequested != 0 &&
            this.editDetail.retroVisitsRequested != null)
        : true;

      const isExternalUserAnd0TotalVisists = this.isExternalUser && (this.editDetail.totalVisitsRequested === 0 || this.editDetail.totalVisitsRequested === null);

      this.requestedVisitsValid =
        hasRetroPeriod
          ? !isExternalUserAnd0TotalVisists && isRetroVisitsAmountValid
          : true;
    });
  }

  private loadServiceLineEditFormFieldChanges(formValue): void {

    if (formValue.additionalComments !== undefined) {
      this.editDetail.additionalComments = formValue.additionalComments;
    }
    formValue.reviewPeriodType = this.isReviewPeriodDefaultA ? this.reviewPeriodA : formValue.reviewPeriodType;

    if (this.hh30DayReviewPeriods && formValue.reviewPeriodType) {
      this.editDetail.reviewPeriodType = formValue.reviewPeriodType;
      const reviewPeriod = this.selectedCertPeriod.reviewPeriods.find(x => x.reviewPeriodType == formValue.reviewPeriodType);
      this.editDetail.reviewPeriodStartDate = reviewPeriod?.startDate;
      this.editDetail.reviewPeriodEndDate = reviewPeriod?.endDate;
    }

    if (this.currentEditStep >= 3) {
      const minTimely = this.isExternalUser ? 1 : 0;

      this.editDetail.timelyVisitsRequested = formValue.timelyVisitsRequested;

      if (this.editDetail.timelyVisitsRequested != null &&
        ((this.editDetail.retroDateRange === this.noRetroPeriodMessage &&
          this.editDetail.timelyVisitsRequested >= minTimely) ||
          (this.editDetail.retroDateRange !== this.noRetroPeriodMessage && this.editDetail.timelyVisitsRequested >= 0)) &&
        this.currentEditStep === 3) {
        this.currentEditStep = 4;
        this.setEditInstructions(this.currentEditStep);
      }
      this.calculateTotalVisitsRequested();
    }

    if (this.currentEditStep >= 2) {
      this.editDetail.retroVisitsRequested = formValue.retroVisitsRequested;

      if (this.editDetail.retroVisitsRequested !== null && this.editDetail.retroVisitsRequested >= 0 && this.currentEditStep === 2) {
        this.currentEditStep = 3;
        this.setEditInstructions(this.currentEditStep);
      }

      this.calculateTotalVisitsRequested();
    }

    if (this.currentEditStep >= 1) {
      if (formValue.dateLastVisitDeliveredNA === true) {
        this.editDetail.dateLastVisitDelivered = null;

        if (!this.retroDateRangeEffectiveToDate) {
          this.editDetail.retroDateRange = this.noRetroPeriodMessage;
        }
      } else if (formValue.dateLastVisitDelivered != null) {
        this.editDetail.dateLastVisitDelivered = formValue.dateLastVisitDelivered ?
          moment(new Date(formValue.dateLastVisitDelivered).setHours(0)).format('L') :
          null;
      } else if (this.currentEditStep >= 1) {
        this.currentEditStep = 1;
        this.setEditInstructions(this.currentEditStep);
      }
    }

    if (this.currentEditStep >= 0 && this.requestType === this.subsequentRequestType) {
      this.editDetail.urgency = formValue.urgency;

      if (this.editDetail.urgency !== null && this.currentEditStep === 0) {
        this.currentEditStep = 1;
        this.setEditInstructions(this.currentEditStep);
      }
    }
  }

  private setEditInstructions(step: number): void {
    switch (step) {
      case 0:
        this.editInstructions = `Select the service line's Urgency.`;
        this.showHeaderForDateSelect = false;
        break;
      case 1:
        this.showHeaderForDateSelect = this.showNA ? false : true;
        break;
      case 2:
        this.editInstructions = `Services delivered between these dates are considered Retroacive.
        Enter the number of Retroactive visits. Value must greater than zero.`;
        this.showHeaderForDateSelect = false;
        break;
      case 3:
        this.editInstructions = 'Enter the number of timely visits.  This number is the Total Visits Requested - Number of Retro Visits Requested.';
        this.showHeaderForDateSelect = false;
        break;
      case 4:
        this.editInstructions = 'Click the save button to save the service line.';
        break;
    }
  }

  calculateTotalVisitsRequested() {
    const retroVisits = this.editDetail.retroVisitsRequested ? this.editDetail.retroVisitsRequested : 0;
    const timelyVisits = this.editDetail.timelyVisitsRequested ? this.editDetail.timelyVisitsRequested : 0;
    this.editDetail.totalVisitsRequested = retroVisits + timelyVisits;
  }

  checkVisitCountForExternalUser(event) {
    this.isDisabled = event && event.value === '0' && this.isExternalUser;
  }

  createRetroactiveDateRangeCalc(): RetroactiveDateRangeCalc {
    const dateTimeFaxRecieved = moment.utc(this.dateTimeFaxReceived).local().format('L');
    const dateLastVisitDelivered = this.editDetail.dateLastVisitDelivered ?
      moment(new Date(this.editDetail.dateLastVisitDelivered).setHours(0)).format('L') :
      null;

    return {
      dateLastVisitDelivered,
      dateTimeFaxRecieved,
      requestType: this.requestType,
      certPeriodEffectiveToDate: this.selectedCertPeriod.effectiveToDate,
      startOfCareDate: this.startOfCareDate,
      retroDays: this.editDetail.retroDays,
      ...(this.hasContractFlow && { contractStartDate: this.contractStartDate }),
      reviewPeriodStartDate: this.selectedReviewPeriod?.startDate,
      reviewPeriodEndDate: this.selectedReviewPeriod?.endDate,
      reviewPeriodType: this.selectedReviewPeriod?.reviewPeriodType,
      participationStatus: this.participationStatus,
      reimbursementType: this.reimbursementType,
      isServiceLineReauth: !this.showNA,
      authorizationID: this.selectedCertPeriod.authorizationId,
    };
  }

  private checkRetroRange(): void {
    const [retroDateRangeStart, retroDateRangeEnd] = this.editDetail.retroDateRange.split('-').map(s => new Date(s.trim()));

    if(retroDateRangeStart > retroDateRangeEnd) {
      this.editDetail.retroDateRange = this.noRetroPeriodMessage;
      this.retroEffectiveDatePriorToSelectedReviewPeriod = true;
    } else if (retroDateRangeStart <= retroDateRangeEnd) {
      this.retroEffectiveDatePriorToSelectedReviewPeriod = false;
    }

    if (this.editDetail.retroDateRange === this.noRetroPeriodMessage) {
      this.serviceLineEditForm.controls['retroVisitsRequested'].clearValidators();
      this.timelyVisitsRequestedControl
        .setValidators(this.isInitialServiceLinesSubsequentRequest
          ? [Validators.required, Validators.min(1), Validators.max(9999), Validators.maxLength(4), Validators.pattern('[0-9]*')]
          : [Validators.required, Validators.min(0), Validators.max(9999), Validators.maxLength(4), Validators.pattern('[0-9]*')]);
      this.currentEditStep = 3;
    } else {
      if (this.requestType == 'Initial'){
        if(this.serviceLineEditForm.controls['dateLastVisitDelivered'].value > retroDateRangeEnd)
        {
           this.serviceLineEditForm.controls['retroVisitsRequested']
          .setValidators([Validators.required, Validators.min(0), Validators.max(999), Validators.maxLength(3),Validators.pattern('[0-9]*')]);
        }
        else{
        this.serviceLineEditForm.controls['retroVisitsRequested']
          .setValidators([Validators.required, Validators.min(1), Validators.max(999), Validators.maxLength(3), Validators.pattern('[0-9]*')]);
        }
      } else {
        this.serviceLineEditForm.controls['retroVisitsRequested']
          .setValidators([Validators.required, Validators.min(0), Validators.max(999), Validators.maxLength(3), Validators.pattern('[0-9]*')]);
      }
      this.timelyVisitsRequestedControl
        .setValidators([Validators.required, Validators.min(0), Validators.max(9999), Validators.maxLength(4), Validators.pattern('[0-9]*')]);
      this.currentEditStep = 2;
    }

    this.setEditInstructions(this.currentEditStep);
    this.serviceLineEditForm.controls['retroVisitsRequested'].setValue(null);
    this.timelyVisitsRequestedControl.setValue(null);
  }

  private isOnOrAfter(firstDate: string, secondDate:string): boolean {
    return firstDate && secondDate && new Date(firstDate).getTime() >= new Date(secondDate).getTime();
  }

  checkAndDisableTimelyVisits(): void {
    this.stopWatchingRetroVisitsRequested();
    this.stopWatchingTimelyVisitsRequested();
    this.showTimelyAndTotalVisits = true;
    if ((this.editDetail.retroDateRange !== this.noRetroPeriodMessage)
      && ((this.datePipe.transform(this.selectedReviewPeriod.endDate) === this.datePipe.transform(this.retroDateRangeEffectiveToDate))
      || (this.isHHRequestProcessUpdates && this.dateLastVisitDeliveredNAControl.value && this.isOnOrAfter(this.retroDateRangeEffectiveToDate, this.selectedReviewPeriod.endDate)))) {
      this.timelyVisitsRequestedControl.setValue(0);
      this.timelyVisitsRequestedControl.disable();
      this.serviceLineEditForm.controls['retroVisitsRequested']
        .setValidators([Validators.required, Validators.min(1), Validators.max(999), Validators.maxLength(3), Validators.pattern('[0-9]*')]);
    } else if (this.editDetail.retroDateRange === this.noRetroPeriodMessage) {
      this.timelyVisitsRequestedControl.setValue(null);
      this.timelyVisitsRequestedControl.enable();
    } else {
      this.showTimelyAndTotalVisits = false;
      this.watchRetroVisitsRequested();
    }
  }

  private watchRetroVisitsRequested(): void {
    this.unsubscribeRetroVisitsRequested = this.serviceLineEditForm.get('retroVisitsRequested').valueChanges.subscribe(val => {
      if (val >= 0) {
        this.showTimelyAndTotalVisits = true;
        this.timelyVisitsRequestedControl.setValue(null);
        this.timelyVisitsRequestedControl.enable();
      }
      this.watchTimelyVisitsRequested();
    });
  }

  private stopWatchingRetroVisitsRequested(): void {
    if (this.unsubscribeRetroVisitsRequested) {
      this.unsubscribeRetroVisitsRequested.unsubscribe();
    }
  }

  private watchTimelyVisitsRequested(): void {
    this.unsubscribeTimelyVisitsRequested = this.serviceLineEditForm.get('timelyVisitsRequested').valueChanges.subscribe(val => {
      if (val >= 0) {
        this.stopWatchingRetroVisitsRequested();
      }
    });
  }

  private stopWatchingTimelyVisitsRequested(): void {
    if (this.unsubscribeTimelyVisitsRequested) {
      this.unsubscribeTimelyVisitsRequested.unsubscribe();
    }
  }

  handleDateLastVisitDeliveredSelected(): void {
    this.dateLastVisitDeliveredNAControl.setValue(false);

    if (this.isHHRequestProcessUpdates && this.noRetroVisitsForCSD) {
      this.editDetail.retroDateRange = this.noRetroPeriodMessage;
      this.updateValidityAndRecheck();
    } else {
      const retroactiveDateRangeCalc = this.createRetroactiveDateRangeCalc();

      this.isGettingRetroDateRange = true;
      this.authorizationRequestService.calculateRetroDateRanges(this.tenantId, retroactiveDateRangeCalc)
        .pipe(
          finalize(() => this.isGettingRetroDateRange = false)
        )
        .subscribe(resp => {
          this.editDetail.retroDateRange = resp.retroDateRange;
          this.retroDateRangeEffectiveToDate = resp.effectiveTo;
          this.updateValidityAndRecheck();
        });
    }
  }

  private updateValidityAndRecheck(): void {
    this.checkRetroRange();
    this.serviceLineEditForm.updateValueAndValidity();

    if(this.hh30DayReviewPeriods){
      this.checkAndDisableTimelyVisits();
    }
  }

  handleDateLastVisitNASelected(event): void {
    if (event.checked) {
      this.editDetail.retroDateRange = this.noRetroPeriodMessage;
      this.retroDateRangeEffectiveToDate = '';
      this.editDetail.dateLastVisitDelivered = null;
      this.serviceLineEditForm.controls['dateLastVisitDelivered'].clearValidators();
      this.serviceLineEditForm.controls['dateLastVisitDelivered'].setValue(null);
    } else {
      this.serviceLineEditForm.controls['dateLastVisitDelivered'].setValidators(Validators.required);
    }

    if (event.checked && this.isHHRequestProcessUpdates && !this.noRetroVisitsForCSD) {
      const retroactiveDateRangeCalc = this.createRetroactiveDateRangeCalc();

      this.isGettingRetroDateRange = true;

      this.authorizationRequestService.calculateRetroDateRanges(this.tenantId, retroactiveDateRangeCalc)
        .pipe(
          finalize(() => this.isGettingRetroDateRange = false),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(resp => {
          if (this.isOnOrAfter(resp?.effectiveTo, this.selectedReviewPeriod.endDate)) {
            this.editDetail.retroDateRange = `${this.datePipe.transform(resp.reviewPeriodStartDate, "MM/dd/yyyy")} - ${this.datePipe.transform(resp.reviewPeriodEndDate, "MM/dd/yyyy")}`;
            this.retroDateRangeEffectiveToDate = resp.effectiveTo;
          }

          this.updateValidityAndRecheck();
        });
    } else {
      this.updateValidityAndRecheck();
    }
}

  save(): void {
    this.memberCriteriaFormArray.value?.splice(3,1);
    const selectedMemberCriteriasIds = this.memberCriteriaFormArray.value
    .filter(el => el.selected)
    .map(el => el.value.id);

    const reviewPeriod = this.isReviewPeriodDefaultA
                          ? this.reviewPeriodA
                          : this.reviewPeriodTypeControl.value;

    const reviewData = this.selectedReviewPeriod

    if(this.editDetail.reviewPeriodType){
      reviewData.reviewPeriodType = this.editDetail.reviewPeriodType;
      reviewData.startDate = this.editDetail.reviewPeriodStartDate;
      reviewData.endDate = this.editDetail.reviewPeriodEndDate;
    }

    const data = {selectedMemberCriteriasIds, editDetail: this.editDetail, reviewPeriod, reviewData};

    this.allowToChooseCriteria = false;
    this.ref.close(data);
  }

  cancel(): void {
    this.ref.close();
  }

  getReviewPeriodAType(): string{
    const reviewPeriodA = this.reviewPeriodOptions.find(x => x.value == this.reviewPeriodA);
    return reviewPeriodA.label;
  }

  setInfoTitleMessage(): string{
   const startDateReviewPeriodB = this.subtractReviewPeriodB7Days();
   const date = this.datePipe.transform(startDateReviewPeriodB, "MM/dd/yyyy");
   return `You are requesting visits for review period A. Next review period dates open on ${date}.`;
  }

  isReviewPeriodADefaulted(){
   const startDateReviewPeriodB = this.subtractReviewPeriodB7Days();
   const dateOfRequestReceived = new Date(this.serviceLineEditForm.value.dateTimeFaxReceived);
   this.isReviewPeriodDefaultA = dateOfRequestReceived < startDateReviewPeriodB && !this.reviewPeriodException;
   if(this.isReviewPeriodDefaultA)
    this.reviewPeriodFormWatch(this.reviewPeriodA);
  }

  // subtract 7 days from Review Period B Start Date
  subtractReviewPeriodB7Days(): Date{
   const startDateReviewPeriodB = new Date(this.reviewPeriods?.find(x => x.reviewPeriodType == "ReviewPeriodB")?.startDate);
   startDateReviewPeriodB?.setDate(startDateReviewPeriodB?.getDate() - 7);
   return startDateReviewPeriodB;
  }

  reviewPeriodFormWatch(type: string){
    const reviewPeriod = this.reviewPeriods.find(y => y.reviewPeriodType === type);

      this.showDateDisciplineFirstVisit = true;

      if (reviewPeriod?.startDate) {
        if (this.isInitRequest && !this.createNextReviewPeriodClicked){
          this.minDateLastVisitDeliveredDate = new Date(this.datePipe.transform(reviewPeriod.startDate, 'MM/dd/yyyy'));
          this.maxDateLastVisitDeliveredDate = new Date(this.datePipe.transform(reviewPeriod.endDate, 'MM/dd/yyyy'));
        } else {
          const minDate = this.showNA ? reviewPeriod.startDate : this.editDetail.startOfCareDate;

          this.minDateLastVisitDeliveredDate = new Date(this.datePipe.transform(minDate, 'MM/dd/yyyy'));

          if (this.isInitRequest || (!this.isInitRequest && !this.showNA)) {
            this.maxDateLastVisitDeliveredDate = new Date(new Date(this.datePipe.transform(reviewPeriod.endDate, 'MM/dd/yyyy')).getTime() - (1000 * 60 * 60 * 24));
          } else {
            this.maxDateLastVisitDeliveredDate = new Date(new Date(this.datePipe.transform(reviewPeriod.endDate, 'MM/dd/yyyy')));
          }
        }
      }

      this.selectedReviewPeriod = reviewPeriod;
      this.clearOutEditDetail();
  }
}
